import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Mail, Smartphone, AlertCircle, RefreshCw, Wifi, WifiOff } from 'lucide-react';

interface NotificationPreferences {
  email_enabled: boolean;
  sms_enabled: boolean;
  email_on_fund_released: boolean;
  email_on_withdrawal: boolean;
  email_on_order_paid: boolean;
  email_on_balance_threshold: boolean;
  balance_threshold_enabled: boolean;
  balance_threshold_amount_cents: number;
}

export default function NotificationSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'online' | 'offline' | 'checking'>('checking');
  const { toast } = useToast();

  useEffect(() => {
    fetchPreferences();
    
    // Vérifier la connexion
    const checkConnection = async () => {
      try {
        setConnectionStatus('checking');
        const { data, error } = await supabase.from('profiles').select('id').limit(1);
        setConnectionStatus(error ? 'offline' : 'online');
      } catch {
        setConnectionStatus('offline');
      }
    };
    
    checkConnection();
    
    // Vérifier la connexion périodiquement
    const connectionInterval = setInterval(checkConnection, 30000);
    
    return () => clearInterval(connectionInterval);
  }, []);

  const retryFetch = () => {
    setLoading(true);
    fetchPreferences();
  };

  const fetchPreferences = async (retryCount = 0) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('[NotificationSettings] No user found');
        return;
      }

      console.log('[NotificationSettings] Fetching preferences for user:', user.id);

      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.log('[NotificationSettings] Fetch error:', error);
        
        // Si pas de préférences trouvées, créer des préférences par défaut
        if (error.code === 'PGRST116') {
          console.log('[NotificationSettings] No preferences found, creating defaults');
          const defaultPrefs = {
            email_enabled: true,
            sms_enabled: false,
            email_on_fund_released: true,
            email_on_withdrawal: true,
            email_on_order_paid: true,
            email_on_balance_threshold: true,
            balance_threshold_enabled: true,
            balance_threshold_amount_cents: 10000000, // 100,000 CDF
          };
          
          // Tenter de créer les préférences par défaut
          try {
            const { error: insertError } = await supabase
              .from('notification_preferences')
              .insert({
                user_id: user.id,
                ...defaultPrefs
              });
              
            if (insertError) {
              console.log('[NotificationSettings] Insert error:', insertError);
              // Si échec de création, utiliser les valeurs par défaut sans sauvegarder
              setPreferences(defaultPrefs);
            } else {
              console.log('[NotificationSettings] Default preferences created');
              setPreferences(defaultPrefs);
            }
          } catch (insertErr) {
            console.error('[NotificationSettings] Failed to create defaults:', insertErr);
            setPreferences(defaultPrefs);
          }
          return;
        }
        
        // Retry automatique pour les erreurs temporaires
        if ((error.message?.includes('connection') || error.message?.includes('timeout')) && retryCount < 2) {
          console.log(`[NotificationSettings] Retrying fetch (${retryCount + 1}/2)...`);
          setTimeout(() => fetchPreferences(retryCount + 1), 1000);
          return;
        }
        
        throw error;
      }

      console.log('[NotificationSettings] Preferences loaded:', data);
      setPreferences(data || {
        email_enabled: true,
        sms_enabled: false,
        email_on_fund_released: true,
        email_on_withdrawal: true,
        email_on_order_paid: true,
        email_on_balance_threshold: true,
        balance_threshold_enabled: true,
        balance_threshold_amount_cents: 10000000, // 100,000 CDF
      });
      
    } catch (error: any) {
      console.error('[NotificationSettings] Error fetching preferences:', error);
      
      let errorMessage = 'Impossible de charger vos préférences';
      
      if (error.message?.includes('RLS') || error.message?.includes('permission')) {
        errorMessage = 'Problème d\'autorisation. Reconnectez-vous et réessayez.';
      } else if (error.message?.includes('connection') || error.message?.includes('network')) {
        errorMessage = 'Problème de connexion. Vérifiez votre réseau et actualisez la page.';
      }
      
      toast({
        title: 'Erreur de chargement',
        description: errorMessage,
        variant: 'destructive',
      });
      
      // Utiliser des préférences par défaut en cas d'erreur
      setPreferences({
        email_enabled: true,
        sms_enabled: false,
        email_on_fund_released: true,
        email_on_withdrawal: true,
        email_on_order_paid: true,
        email_on_balance_threshold: true,
        balance_threshold_enabled: true,
        balance_threshold_amount_cents: 10000000,
      });
      
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async (retryCount = 0) => {
    if (!preferences) return;

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Utilisateur non authentifié');
      }

      console.log('[NotificationSettings] Saving preferences for user:', user.id);
      
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          ...preferences,
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('[NotificationSettings] Database error:', error);
        
        // Retry automatique pour les erreurs temporaires
        if ((error.message.includes('connection') || error.message.includes('timeout')) && retryCount < 2) {
          console.log(`[NotificationSettings] Retrying save (${retryCount + 1}/2)...`);
          setTimeout(() => savePreferences(retryCount + 1), 1000);
          return;
        }
        
        throw error;
      }

      console.log('[NotificationSettings] Preferences saved successfully');
      toast({
        title: 'Préférences enregistrées',
        description: 'Vos paramètres de notification ont été mis à jour avec succès',
      });
      
    } catch (error: any) {
      console.error('[NotificationSettings] Error saving preferences:', error);
      
      let errorMessage = 'Impossible d\'enregistrer vos préférences';
      
      if (error.message?.includes('RLS')) {
        errorMessage = 'Problème d\'autorisation. Reconnectez-vous et réessayez.';
      } else if (error.message?.includes('connection') || error.message?.includes('network')) {
        errorMessage = 'Problème de connexion. Vérifiez votre réseau et réessayez.';
      } else if (error.message?.includes('duplicate key')) {
        errorMessage = 'Conflit de données. Actualisez la page et réessayez.';
      }
      
      toast({
        title: 'Erreur de sauvegarde',
        description: errorMessage,
        variant: 'destructive',
      });
      
      // Logger l'erreur pour debug
      if (typeof window !== 'undefined') {
        localStorage.setItem('last_notification_error', JSON.stringify({
          error: error.message,
          timestamp: new Date().toISOString(),
          preferences: preferences
        }));
      }
      
    } finally {
      setSaving(false);
    }
  };

  const updatePreference = (key: keyof NotificationPreferences, value: any) => {
    if (!preferences) return;
    setPreferences({ ...preferences, [key]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!preferences) return null;

  const formatAmount = (cents: number) => {
    return (cents / 100).toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  return (
    <div className="space-y-6">
      {/* Indicateur de connexion */}
      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
        <div className="flex items-center gap-2">
          {connectionStatus === 'online' ? (
            <>
              <Wifi className="h-4 w-4 text-green-600" />
              <span className="text-sm text-green-600">Connexion active</span>
            </>
          ) : connectionStatus === 'offline' ? (
            <>
              <WifiOff className="h-4 w-4 text-red-600" />
              <span className="text-sm text-red-600">Connexion limitée</span>
            </>
          ) : (
            <>
              <Loader2 className="h-4 w-4 animate-spin text-yellow-600" />
              <span className="text-sm text-yellow-600">Vérification...</span>
            </>
          )}
        </div>
        {connectionStatus === 'offline' && (
          <Button variant="ghost" size="sm" onClick={retryFetch} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Réessayer
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Notifications par email
          </CardTitle>
          <CardDescription>
            Recevez des notifications par email pour rester informé
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="email_enabled" className="font-medium">
                Activer les emails
              </Label>
              <p className="text-sm text-muted-foreground">
                Recevoir des notifications par email
              </p>
            </div>
            <Switch
              id="email_enabled"
              checked={preferences.email_enabled}
              onCheckedChange={(checked) => updatePreference('email_enabled', checked)}
            />
          </div>

          {preferences.email_enabled && (
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center justify-between">
                <Label htmlFor="email_on_fund_released" className="font-normal">
                  Fonds libérés
                </Label>
                <Switch
                  id="email_on_fund_released"
                  checked={preferences.email_on_fund_released}
                  onCheckedChange={(checked) =>
                    updatePreference('email_on_fund_released', checked)
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="email_on_withdrawal" className="font-normal">
                  Retraits approuvés/refusés
                </Label>
                <Switch
                  id="email_on_withdrawal"
                  checked={preferences.email_on_withdrawal}
                  onCheckedChange={(checked) =>
                    updatePreference('email_on_withdrawal', checked)
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="email_on_order_paid" className="font-normal">
                  Commandes payées
                </Label>
                <Switch
                  id="email_on_order_paid"
                  checked={preferences.email_on_order_paid}
                  onCheckedChange={(checked) =>
                    updatePreference('email_on_order_paid', checked)
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="email_on_balance_threshold" className="font-normal">
                  Seuil de solde atteint
                </Label>
                <Switch
                  id="email_on_balance_threshold"
                  checked={preferences.email_on_balance_threshold}
                  onCheckedChange={(checked) =>
                    updatePreference('email_on_balance_threshold', checked)
                  }
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Notifications par SMS
          </CardTitle>
          <CardDescription>
            Recevez des SMS pour les événements importants (bientôt disponible)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="sms_enabled" className="font-medium text-muted-foreground">
                Activer les SMS
              </Label>
              <p className="text-sm text-muted-foreground">
                Fonctionnalité en cours de développement
              </p>
            </div>
            <Switch
              id="sms_enabled"
              checked={preferences.sms_enabled}
              disabled
              onCheckedChange={(checked) => updatePreference('sms_enabled', checked)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Alertes de solde
          </CardTitle>
          <CardDescription>
            Recevez une alerte lorsque votre solde disponible dépasse un certain montant
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="balance_threshold_enabled" className="font-medium">
              Activer les alertes de solde
            </Label>
            <Switch
              id="balance_threshold_enabled"
              checked={preferences.balance_threshold_enabled}
              onCheckedChange={(checked) =>
                updatePreference('balance_threshold_enabled', checked)
              }
            />
          </div>

          {preferences.balance_threshold_enabled && (
            <div className="space-y-2 pt-2 border-t">
              <Label htmlFor="balance_threshold">
                Seuil d'alerte (CDF)
              </Label>
              <Input
                id="balance_threshold"
                type="number"
                step="1000"
                value={preferences.balance_threshold_amount_cents / 100}
                onChange={(e) =>
                  updatePreference(
                    'balance_threshold_amount_cents',
                    Math.round(parseFloat(e.target.value) * 100)
                  )
                }
                className="max-w-xs"
              />
              <p className="text-sm text-muted-foreground">
                Vous serez alerté lorsque votre solde disponible atteindra{' '}
                <span className="font-medium">
                  {formatAmount(preferences.balance_threshold_amount_cents)} CDF
                </span>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={savePreferences} disabled={saving}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Enregistrer les préférences
        </Button>
      </div>
    </div>
  );
}
